"use client";

import React, { useEffect, useState, useRef, useTransition } from "react";
import { CircleCheck, CircleAlert, Loader2, Send, RefreshCw, Building2, Mail, Phone, User, Layers, FileText, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  help?: string;
  required?: boolean;
  options?: Array<{
    value: string;
    label: string;
    price?: number;
  }>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  description: string;
  availability?: {
    accepting: boolean;
    state: string;
    message: string;
  };
  fields: FormFieldDefinition[];
  submit?: {
    label: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
    challenge?: any;
  };
}
const consultationCopy = {
  eyebrow: {
    en: "Engineering Services",
    zh: "工程咨询服务",
    es: "Servicios de ingeniería",
    de: "Ingenieurdienstleistungen",
    fr: "Services d'ingénierie",
    pt: "Serviços de engenharia"
  },
  h2: {
    en: "Request a technical consultation",
    zh: "申请工程技术咨询",
    es: "Solicite una consulta técnica",
    de: "Technische Beratung anfordern",
    fr: "Demander une consultation technique",
    pt: "Solicitar consultoria técnica"
  },
  lead: {
    en: "Submit your process parameters and operational specs directly to our metallurgical engineering team for custom weave, alloy selection, and micron rating validation.",
    zh: "直接向我们的冶金工程团队提交您的工艺参数与运行规格，获取定制编织结构、合金选型及过滤精度验证。",
    es: "Envíe sus parámetros de proceso y especificaciones operativas directamente a nuestro equipo de ingeniería metalúrgica para diseño de tejido a medida, selección de aleación y validación de micras.",
    de: "Übermitteln Sie Ihre Prozessparameter und Betriebsspezifikationen direkt an unser metallurgisches Ingenieurteam zur kundenspezifischen Gewebeauslegung, Legierungswahl und Mikron-Validierung.",
    fr: "Transmettez directement vos paramètres de procédé et spécifications à notre équipe d'ingénierie métallurgique pour une armure sur mesure, le choix d'alliage et la validation des microns.",
    pt: "Envie seus parâmetros de processo e especificações operacionais diretamente para nossa equipe de engenharia metalúrgica para tecelagem sob medida, seleção de liga e validação de mícrons."
  },
  channelTitle: {
    en: "Direct Engineering Specification Intake",
    zh: "工程规格直联申报通道",
    es: "Canal directo de especificaciones de ingeniería",
    de: "Direkter Kanal für technische Spezifikationen",
    fr: "Canal direct de transmission des spécifications",
    pt: "Canal direto para especificações de engenharia"
  },
  loadErrorTitle: {
    en: "Unable to load intake form",
    zh: "无法加载咨询表单",
    es: "No se pudo cargar el formulario",
    de: "Formular konnte nicht geladen werden",
    fr: "Impossible de charger le formulaire",
    pt: "Não foi possível carregar o formulário"
  },
  loadErrorMsg: {
    en: "Unable to load technical consultation form. Please refresh or contact engineering directly.",
    zh: "无法加载技术咨询表单。请刷新页面或直接联系工程团队。",
    es: "No se puede cargar el formulario de consulta técnica. Por favor actualice o contacte directamente al equipo de ingeniería.",
    de: "Das Formular für technische Beratung konnte nicht geladen werden. Bitte aktualisieren Sie die Seite oder wenden Sie sich direkt an die Technik.",
    fr: "Impossible de charger le formulaire de consultation technique. Veuillez actualiser ou contacter directement l'ingénierie.",
    pt: "Não foi possível carregar o formulário de consultoria técnica. Atualize a página ou contate a engenharia diretamente."
  },
  retryBtn: {
    en: "Retry loading form",
    zh: "重试加载表单",
    es: "Reintentar carga",
    de: "Erneut versuchen",
    fr: "Réessayer le chargement",
    pt: "Tentar carregar novamente"
  },
  successTitle: {
    en: "Consultation Request Received",
    zh: "咨询需求已成功接收",
    es: "Solicitud de consulta recibida",
    de: "Beratungsanfrage erhalten",
    fr: "Demande de consultation reçue",
    pt: "Solicitação de consultoria recebida"
  },
  successFallback: {
    en: "Thank you. Your technical consultation request has been received — our engineering team will follow up shortly.",
    zh: "感谢您的咨询。您的技术咨询请求已收到 — 我们的工程团队将尽快跟进。",
    es: "Gracias. Su solicitud de consulta técnica ha sido recibida; nuestro equipo de ingeniería se comunicará a la brevedad.",
    de: "Vielen Dank. Ihre technische Beratungsanfrage ist eingegangen – unser Ingenieurteam wird sich in Kürze bei Ihnen melden.",
    fr: "Merci. Votre demande de consultation technique a bien été reçue — notre équipe d'ingénierie vous recontactera rapidement.",
    pt: "Obrigado. Sua solicitação de consultoria técnica foi recebida — nossa equipe de engenharia entrará em contato em breve."
  },
  submitAnother: {
    en: "Submit another inquiry",
    zh: "提交新的咨询需求",
    es: "Enviar otra consulta",
    de: "Weitere Anfrage senden",
    fr: "Envoyer une autre demande",
    pt: "Enviar outra consulta"
  },
  formErrorTitle: {
    en: "Form submission error",
    zh: "表单提交失败",
    es: "Error de envío del formulario",
    de: "Fehler beim Absenden des Formulars",
    fr: "Erreur d'envoi du formulaire",
    pt: "Erro no envio do formulário"
  },
  tokenExpired: {
    en: "Security token expired. Please try submitting again.",
    zh: "安全验证令牌已过期，请重新尝试提交。",
    es: "El token de seguridad ha expirado. Por favor intente enviar de nuevo.",
    de: "Das Sicherheitstoken ist abgelaufen. Bitte versuchen Sie es erneut.",
    fr: "Jeton de sécurité expiré. Veuillez réessayer de soumettre.",
    pt: "O token de segurança expirou. Tente enviar novamente."
  },
  generalSubmitError: {
    en: "Submission was not accepted. Please review and try again.",
    zh: "表单提交未能成功接收，请核对信息后重试。",
    es: "El envío no fue aceptado. Por favor revise y vuelva a intentarlo.",
    de: "Die Übermittlung wurde nicht angenommen. Bitte überprüfen Sie die Angaben und versuchen Sie es erneut.",
    fr: "La soumission n'a pas été acceptée. Veuillez vérifier vos données et réessayer.",
    pt: "O envio não foi aceito. Revise os dados e tente novamente."
  },
  networkError: {
    en: "A network or server error occurred while sending your request. Please try again.",
    zh: "发送请求时发生网络或服务器错误，请稍后重试。",
    es: "Ocurrió un error de red o servidor al enviar su solicitud. Por favor intente más tarde.",
    de: "Beim Senden Ihrer Anfrage ist ein Netzwerk- oder Serverfehler aufgetreten. Bitte versuchen Sie es später erneut.",
    fr: "Une erreur réseau ou serveur s'est produite lors de l'envoi de votre demande. Veuillez réessayer.",
    pt: "Ocorreu um erro de rede ou servidor ao enviar sua solicitação. Tente novamente mais tarde."
  },
  labelFullName: {
    en: "Full Name",
    zh: "联系人姓名",
    es: "Nombre completo",
    de: "Vollständiger Name",
    fr: "Nom complet",
    pt: "Nome completo"
  },
  placeholderFullName: {
    en: "Dr. / Eng. Jane Doe",
    zh: "例如：王工程师 / 李经理",
    es: "p. ej. Ing. Carlos Pérez",
    de: "z. B. Dr. / Ing. Thomas Müller",
    fr: "ex. Dr. / Ing. Sophie Martin",
    pt: "ex. Eng. Carlos Souza"
  },
  labelCompany: {
    en: "Company / Facility",
    zh: "企业 / 生产工厂名称",
    es: "Empresa / Planta",
    de: "Unternehmen / Werk",
    fr: "Entreprise / Usine",
    pt: "Empresa / Instalação"
  },
  placeholderCompany: {
    en: "Industrial Processing Corp.",
    zh: "例如：某某流体控制技术有限公司",
    es: "p. ej. Industrias de Procesamiento S.A.",
    de: "z. B. Prozesstechnik GmbH",
    fr: "ex. Procédés Industriels SAS",
    pt: "ex. Processamento Industrial Ltda."
  },
  labelEmail: {
    en: "Work Email",
    zh: "工作电子邮箱",
    es: "Correo corporativo",
    de: "Geschäftliche E-Mail",
    fr: "E-mail professionnel",
    pt: "E-mail corporativo"
  },
  placeholderEmail: {
    en: "name@company.com",
    zh: "name@company.com",
    es: "nombre@empresa.com",
    de: "name@unternehmen.de",
    fr: "nom@entreprise.fr",
    pt: "nome@empresa.com.br"
  },
  labelPhone: {
    en: "Direct Phone",
    zh: "联系电话",
    es: "Teléfono directo",
    de: "Direkttelefon",
    fr: "Téléphone direct",
    pt: "Telefone direto"
  },
  placeholderPhone: {
    en: "+1 (555) 012-3456",
    zh: "+86 138-0000-0000",
    es: "+34 912 345 678",
    de: "+49 30 12345678",
    fr: "+33 1 23 45 67 89",
    pt: "+55 11 91234-5678"
  },
  labelMeshInterest: {
    en: "Product & Specification Category",
    zh: "产品与规格类别",
    es: "Categoría de producto y especificación",
    de: "Produkt- und Spezifikationskategorie",
    fr: "Catégorie de produit et spécification",
    pt: "Categoria de produto e especificação"
  },
  placeholderMeshInterest: {
    en: "Select filtration or mesh category",
    zh: "请选择过滤或丝网规格类别",
    es: "Seleccione categoría de filtración o malla",
    de: "Filtrations- oder Gewebekategorie wählen",
    fr: "Sélectionnez une catégorie de filtration ou de toile",
    pt: "Selecione categoria de filtração ou malha"
  },
  labelProjectDetails: {
    en: "Operating Parameters & Filtration Requirements",
    zh: "运行工况与过滤技术参数",
    es: "Parámetros operativos y requisitos de filtración",
    de: "Betriebsparameter & Filtrationsanforderungen",
    fr: "Paramètres de fonctionnement et exigences de filtration",
    pt: "Parâmetros operacionais e requisitos de filtração"
  },
  placeholderProjectDetails: {
    en: "Detail medium, particle retention target, operating pressure/temp, alloy grade (AISI 304, 316L, Hastelloy), and dimensional requirements...",
    zh: "请详细说明过滤介质、截留精度目标、工作压力与温度、合金材质要求（AISI 304、316L、哈氏合金等）以及尺寸规格...",
    es: "Detalle fluido/medio, retención de partículas objetivo, presión/temperatura de trabajo, calidad de aleación (AISI 304, 316L, Hastelloy) y dimensiones...",
    de: "Geben Sie Details zu Medium, Partikelrückhaltung, Betriebsdruck/-temperatur, Legierung (AISI 304, 316L, Hastelloy) und Abmessungen an...",
    fr: "Précisez le fluide, le seuil de rétention souhaité, la pression/température de service, la nuance d'alliage (AISI 304, 316L, Hastelloy) et les cotes...",
    pt: "Detalhe o fluido, retenção de partículas desejada, pressão/temperatura de operação, grau da liga (AISI 304, 316L, Hastelloy) e dimensões..."
  },
  ndaNotice: {
    en: "Technical submissions remain confidential under standard commercial non-disclosure practice.",
    zh: "所有提交的技术资料均受商业保密规范严格保护。",
    es: "Las presentaciones técnicas se tratan con total confidencialidad bajo normas estándar.",
    de: "Alle übermittelten technischen Daten werden streng vertraulich behandelt.",
    fr: "Les documents techniques transmis sont traités de manière strictement confidentielle.",
    pt: "Todas as informações técnicas enviadas são mantidas sob estrito sigilo comercial."
  },
  btnSubmitting: {
    en: "Submitting to Engineering...",
    zh: "正在提交至工程团队...",
    es: "Enviando a ingeniería...",
    de: "Wird an die Technik übermittelt...",
    fr: "Envoi à l'ingénierie en cours...",
    pt: "Enviando para a engenharia..."
  },
  btnSubmit: {
    en: "Request Technical Review",
    zh: "提交技术评审需求",
    es: "Solicitar revisión técnica",
    de: "Technische Prüfung anfordern",
    fr: "Demander une revue technique",
    pt: "Solicitar revisão técnica"
  },
  valRequired: {
    en: "This field is required.",
    zh: "此项为必填项。",
    es: "Este campo es obligatorio.",
    de: "Dieses Feld ist erforderlich.",
    fr: "Ce champ est obligatoire.",
    pt: "Este campo é obrigatório."
  },
  valEmailInvalid: {
    en: "Please enter a valid email address.",
    zh: "请输入有效的电子邮箱地址。",
    es: "Introduzca una dirección de correo válida.",
    de: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
    fr: "Veuillez saisir une adresse e-mail valide.",
    pt: "Insira um endereço de e-mail válido."
  }
};
const categoryOptionLabels: Record<string, Record<Lang, string>> = {
  sintered_mesh: {
    en: "Sintered Multilayer Wire Mesh",
    zh: "多层烧结丝网 (Sintered Multilayer Mesh)",
    es: "Malla de alambre multicapa sinterizada",
    de: "Mehrlagiges Sinterdrahtgewebe",
    fr: "Toile métallique multicouche frittée",
    pt: "Malha de arame multicamada sinterizada"
  },
  dutch_weave: {
    en: "Dutch Weave Filter Cloth",
    zh: "密纹编织过滤布 (Dutch Weave Filter Cloth)",
    es: "Tela filtrante de tejido holandés (Dutch weave)",
    de: "Tressengewebe / Filtertuch",
    fr: "Toile filtrante tissage hollandais",
    pt: "Tecido filtrante de trama holandesa"
  },
  square_mesh: {
    en: "Industrial Square Woven Mesh",
    zh: "工业方孔编织网 (Square Woven Mesh)",
    es: "Malla tejida cuadrada industrial",
    de: "Industrie-Quadratmaschengewebe",
    fr: "Toile tissée carrée industrielle",
    pt: "Malha tecida quadrada industrial"
  },
  wedge_wire: {
    en: "Wedge Wire & Screen Cylinders",
    zh: "约翰逊楔形丝筛管 / 滤筒 (Wedge Wire Screens)",
    es: "Tamices y cilindros de alambre en cuña",
    de: "Spaltsiebe & Spaltsiebzylinder",
    fr: "Fils profilés et cylindres filtrants",
    pt: "Telas e cilindros de arame em cunha"
  },
  custom_assemblies: {
    en: "Custom Stamped Discs / Screen Packs",
    zh: "定制冲压过滤网片 / 过滤组件 (Screen Packs)",
    es: "Discos estampados a medida / paquetes de filtros",
    de: "Kundenspezifische Filterscheiben / Siebpakete",
    fr: "Disques emboutis sur mesure / packs filtrants",
    pt: "Discos estampados sob medida / pacotes filtrantes"
  },
  other_specialty: {
    en: "Other Specialty Alloy Mesh",
    zh: "特种耐蚀合金丝网 (Specialty Alloy Mesh)",
    es: "Otras mallas de aleaciones especiales",
    de: "Sonstige Gewebe aus Sonderlegierungen",
    fr: "Autres toiles en alliages spéciaux",
    pt: "Outras malhas em ligas especiais"
  }
};
export default function Consultation() {
  const {
    lang
  } = useLanguage();
  const [, startTransition] = useTransition();
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadErrorMsg, setLoadErrorMsg] = useState<string>("");
  const [formData, setFormData] = useState<Record<string, string>>({
    full_name: "",
    company: "",
    email: "",
    phone: "",
    mesh_interest: "",
    project_details: ""
  });
  const [honeypotVal, setHoneypotVal] = useState<string>("");
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submittedSuccess, setSubmittedSuccess] = useState<boolean>(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const fetchFormDefinition = React.useCallback(async () => {
    setLoadState("loading");
    setLoadErrorMsg("");
    setFormError(null);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      const fallbackDef: FormDefinition = {
        id: 1,
        key: "technical-consultation",
        name: "Technical Consultation",
        description: "Direct engineering consultation for industrial mesh filtration",
        availability: {
          accepting: true,
          state: "open",
          message: ""
        },
        fields: [{
          id: "f_full_name",
          key: "full_name",
          type: "text",
          label: "Full Name",
          placeholder: "e.g. Dr. Arthur Miller",
          required: true
        }, {
          id: "f_company",
          key: "company",
          type: "text",
          label: "Company / Organization",
          placeholder: "e.g. Apex Filtration Systems",
          required: true
        }, {
          id: "f_email",
          key: "email",
          type: "email",
          label: "Work Email",
          placeholder: "name@company.com",
          required: true
        }, {
          id: "f_phone",
          key: "phone",
          type: "phone",
          label: "Phone Number",
          placeholder: "+1 (555) 019-2834",
          required: false
        }, {
          id: "f_mesh_interest",
          key: "mesh_interest",
          type: "select",
          label: "Primary Mesh Product Interest",
          placeholder: "Select specification category",
          required: true,
          options: [{
            value: "sintered_mesh",
            label: "Sintered Multilayer Wire Mesh"
          }, {
            value: "dutch_weave",
            label: "Dutch Weave Filter Cloth"
          }, {
            value: "square_mesh",
            label: "Industrial Square Woven Mesh"
          }, {
            value: "wedge_wire",
            label: "Wedge Wire & Screen Cylinders"
          }, {
            value: "custom_assemblies",
            label: "Custom Stamped Discs / Screen Packs"
          }, {
            value: "other_specialty",
            label: "Other Specialty Alloy Mesh"
          }]
        }, {
          id: "f_project_details",
          key: "project_details",
          type: "textarea",
          label: "Operating Conditions & Technical Specifications",
          placeholder: "Provide details on operating temperature, pressure differential, media filtration requirements, micron rating, and alloy preferences...",
          required: true
        }],
        submit: {
          label: "Request Technical Review"
        },
        spam: {
          token: "demo-token",
          honeypot: {
            field: "frms_hp_tech_req"
          },
          min_seconds: 1
        }
      };
      setFormDef(fallbackDef);
      setFetchTimestamp(Date.now());
      setLoadState("ready");
      return;
    }
    try {
      const def = await client.pluginRequest("wvc-forms/v1/forms/technical-consultation");
      if (isMountedRef.current) {
        setFormDef(def);
        setFetchTimestamp(Date.now());
        setLoadState("ready");
      }
    } catch (err: any) {
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const matched = listRes?.items?.find((item: any) => item.key === "technical-consultation" || item.name && item.name.toLowerCase().includes("consultation")) || (listRes?.items?.length === 1 ? listRes.items[0] : null);
          if (matched && matched.key) {
            const resolved = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            if (isMountedRef.current) {
              setFormDef(resolved);
              setFetchTimestamp(Date.now());
              setLoadState("ready");
              return;
            }
          }
        } catch (_) {}
      }
      if (isMountedRef.current) {
        setLoadErrorMsg(pick(lang, consultationCopy.loadErrorMsg));
        setLoadState("error");
      }
    }
  }, [lang]);
  useEffect(() => {
    fetchFormDefinition();
  }, [fetchFormDefinition]);
  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
    if (fieldErrors[key]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[key];
        return next;
      });
    }
    if (formError) setFormError(null);
  };
  const getFieldDef = (key: string): FormFieldDefinition | undefined => {
    return formDef?.fields?.find(f => f.key === key);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || submitting) return;
    setFormError(null);
    setFieldErrors({});
    const errors: Record<string, {
      message: string;
    }> = {};
    const keysToCheck = ["full_name", "company", "email", "phone", "mesh_interest", "project_details"];
    for (const key of keysToCheck) {
      const fDef = getFieldDef(key);
      if (fDef?.required && (!formData[key] || formData[key].trim() === "")) {
        errors[fDef.id || key] = {
          message: fDef.validation?.messages?.required || pick(lang, consultationCopy.valRequired)
        };
      }
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      const emailDef = getFieldDef("email");
      errors[emailDef?.id || "email"] = {
        message: emailDef?.validation?.messages?.invalid || pick(lang, consultationCopy.valEmailInvalid)
      };
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    const minSec = formDef.spam?.min_seconds ?? 3;
    const elapsedSec = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSec < minSec) {
      await new Promise(resolve => setTimeout(resolve, (minSec - elapsedSec + 0.5) * 1000));
    }
    setSubmitting(true);
    const valuesPayload: Record<string, string> = {};
    formDef.fields.forEach(field => {
      if (formData[field.key] !== undefined) {
        valuesPayload[field.id] = formData[field.key];
      }
    });
    const submitPayload: Record<string, any> = {
      values: valuesPayload,
      token: formDef.spam?.token,
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    if (formDef.spam?.honeypot?.field) {
      submitPayload.hp = {
        [formDef.spam.honeypot.field]: honeypotVal
      };
    }
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setTimeout(() => {
        if (isMountedRef.current) {
          startTransition(() => {
            setSubmittedSuccess(true);
            setSubmitting(false);
          });
        }
      }, 800);
      return;
    }
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: submitPayload
      });
      if (res && res.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation.url) {
          window.location.assign(res.confirmation.url);
          return;
        }
        if (res.confirmation?.message) {
          const cleanMsg = res.confirmation.message.replace(/<[^>]*>?/gm, "").trim();
          if (cleanMsg) setConfirmationMessage(cleanMsg);
        }
        startTransition(() => {
          setSubmittedSuccess(true);
          setSubmitting(false);
        });
      } else if (res && res.code === "frms_validation_failed") {
        setFieldErrors(res.errors ?? {});
        setSubmitting(false);
      } else if (res && res.code === "frms_invalid_token") {
        await fetchFormDefinition();
        setFormError(pick(lang, consultationCopy.tokenExpired));
        setSubmitting(false);
      } else {
        setFormError(res?.message || pick(lang, consultationCopy.generalSubmitError));
        setSubmitting(false);
      }
    } catch (err: any) {
      setFormError(err?.message || pick(lang, consultationCopy.networkError));
      setSubmitting(false);
    }
  };
  const fullNameDef = getFieldDef("full_name");
  const companyDef = getFieldDef("company");
  const emailDef = getFieldDef("email");
  const phoneDef = getFieldDef("phone");
  const meshInterestDef = getFieldDef("mesh_interest");
  const projectDetailsDef = getFieldDef("project_details");
  const successMessageText = confirmationMessage || pick(lang, consultationCopy.successFallback);
  return <section id="technicalconsultation" data-nav="light" className="relative w-full bg-background mf-section border-t border-border overflow-hidden">
      <div className="mf-container">
        {/* Header Section */}
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
            <Badge variant="outline" className="mb-3 px-3 py-1 font-mono uppercase mf-caption tracking-wider border-border bg-card">
              {pick(lang, consultationCopy.eyebrow)}
            </Badge>
            <h2 className="mf-h2 tracking-tight text-foreground font-default">
              {pick(lang, consultationCopy.h2)}
            </h2>
            <p className="mt-3 mf-lead text-muted-foreground leading-relaxed">
              {pick(lang, consultationCopy.lead)}
            </p>
          </div>
        </Reveal>

        {/* Form Container Card */}
        <Reveal delay={60}>
          <div className="w-full">
            <Card className="bg-card text-card-foreground border border-border rounded-lg overflow-hidden">
              <CardHeader className="border-b border-border/80 bg-muted/30 py-4 px-6 md:px-8">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
                    <CardTitle className="text-base font-semibold text-foreground">
                      {pick(lang, consultationCopy.channelTitle)}
                    </CardTitle>
                  </div>
                  <CardDescription className="mf-caption font-mono text-muted-foreground">
                    sales@mutualfilter.com
                  </CardDescription>
                </div>
              </CardHeader>

              <CardContent className="p-6 md:p-8">
                {loadState === "loading" && <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-10 w-full rounded-md" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-10 w-full rounded-md" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-10 w-full rounded-md" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-10 w-full rounded-md" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-44" />
                      <Skeleton className="h-10 w-full rounded-md" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-60" />
                      <Skeleton className="h-28 w-full rounded-md" />
                    </div>
                    <Skeleton className="h-11 w-48 rounded-md" />
                  </div>}

                {loadState === "error" && <div className="py-8 text-center space-y-4">
                    <Alert variant="destructive" className="max-w-xl mx-auto text-left">
                      <CircleAlert className="size-4" aria-hidden="true" />
                      <AlertTitle>{pick(lang, consultationCopy.loadErrorTitle)}</AlertTitle>
                      <AlertDescription>
                        {loadErrorMsg || pick(lang, consultationCopy.loadErrorMsg)}
                      </AlertDescription>
                    </Alert>
                    <Button variant="outline" onClick={() => fetchFormDefinition()} className="mt-4 gap-2">
                      <RefreshCw className="size-4" aria-hidden="true" />
                      {pick(lang, consultationCopy.retryBtn)}
                    </Button>
                  </div>}

                {loadState === "ready" && submittedSuccess && <div className="py-10 md:py-14 text-center max-w-xl mx-auto space-y-5">
                    <div className="inline-flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary mb-2">
                      <CircleCheck className="size-8" aria-hidden="true" />
                    </div>
                    <h3 className="mf-h3 font-bold text-foreground tracking-tight">
                      {pick(lang, consultationCopy.successTitle)}
                    </h3>
                    <p className="text-muted-foreground text-base leading-relaxed">
                      {successMessageText}
                    </p>
                    <div className="pt-4 flex justify-center gap-3">
                      <Button variant="outline" onClick={() => {
                    setFormData({
                      full_name: "",
                      company: "",
                      email: "",
                      phone: "",
                      mesh_interest: "",
                      project_details: ""
                    });
                    setSubmittedSuccess(false);
                    fetchFormDefinition();
                  }}>
                        {pick(lang, consultationCopy.submitAnother)}
                      </Button>
                    </div>
                  </div>}

                {loadState === "ready" && !submittedSuccess && <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    {/* Honeypot Decoy */}
                    {formDef?.spam?.honeypot?.field && <div style={{
                  position: "absolute",
                  left: "-9999px",
                  opacity: 0
                }} aria-hidden="true">
                        <label htmlFor={formDef.spam.honeypot.field}>Leave empty</label>
                        <input id={formDef.spam.honeypot.field} type="text" name={formDef.spam.honeypot.field} value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} tabIndex={-1} autoComplete="off" />
                      </div>}

                    {/* Server/General Alert */}
                    {formError && <Alert variant="destructive">
                        <CircleAlert className="size-4" aria-hidden="true" />
                        <AlertTitle>{pick(lang, consultationCopy.formErrorTitle)}</AlertTitle>
                        <AlertDescription>{formError}</AlertDescription>
                      </Alert>}

                    {/* Grid Inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Full Name */}
                      <div className="space-y-2">
                        <Label htmlFor="consult_full_name" className="text-sm font-medium flex items-center justify-between text-foreground">
                          <span className="flex items-center gap-1.5">
                            <User className="size-3.5 text-muted-foreground" aria-hidden="true" />
                            {pick(lang, consultationCopy.labelFullName)}
                            {fullNameDef?.required && <span className="text-destructive">*</span>}
                          </span>
                        </Label>
                        <Input id="consult_full_name" type="text" value={formData.full_name} onChange={e => handleInputChange("full_name", e.target.value)} placeholder={pick(lang, consultationCopy.placeholderFullName)} disabled={submitting} aria-invalid={Boolean(fieldErrors[fullNameDef?.id || "full_name"])} className="bg-card text-card-foreground" />
                        {fieldErrors[fullNameDef?.id || "full_name"] && <p className="mf-caption text-destructive mt-1 font-medium">
                            {fieldErrors[fullNameDef?.id || "full_name"].message}
                          </p>}
                      </div>

                      {/* Company */}
                      <div className="space-y-2">
                        <Label htmlFor="consult_company" className="text-sm font-medium flex items-center justify-between text-foreground">
                          <span className="flex items-center gap-1.5">
                            <Building2 className="size-3.5 text-muted-foreground" aria-hidden="true" />
                            {pick(lang, consultationCopy.labelCompany)}
                            {companyDef?.required && <span className="text-destructive">*</span>}
                          </span>
                        </Label>
                        <Input id="consult_company" type="text" value={formData.company} onChange={e => handleInputChange("company", e.target.value)} placeholder={pick(lang, consultationCopy.placeholderCompany)} disabled={submitting} aria-invalid={Boolean(fieldErrors[companyDef?.id || "company"])} className="bg-card text-card-foreground" />
                        {fieldErrors[companyDef?.id || "company"] && <p className="mf-caption text-destructive mt-1 font-medium">
                            {fieldErrors[companyDef?.id || "company"].message}
                          </p>}
                      </div>

                      {/* Work Email */}
                      <div className="space-y-2">
                        <Label htmlFor="consult_email" className="text-sm font-medium flex items-center justify-between text-foreground">
                          <span className="flex items-center gap-1.5">
                            <Mail className="size-3.5 text-muted-foreground" aria-hidden="true" />
                            {pick(lang, consultationCopy.labelEmail)}
                            {emailDef?.required && <span className="text-destructive">*</span>}
                          </span>
                        </Label>
                        <Input id="consult_email" type="email" value={formData.email} onChange={e => handleInputChange("email", e.target.value)} placeholder={pick(lang, consultationCopy.placeholderEmail)} disabled={submitting} aria-invalid={Boolean(fieldErrors[emailDef?.id || "email"])} className="bg-card text-card-foreground" />
                        {fieldErrors[emailDef?.id || "email"] && <p className="mf-caption text-destructive mt-1 font-medium">
                            {fieldErrors[emailDef?.id || "email"].message}
                          </p>}
                      </div>

                      {/* Phone */}
                      <div className="space-y-2">
                        <Label htmlFor="consult_phone" className="text-sm font-medium flex items-center justify-between text-foreground">
                          <span className="flex items-center gap-1.5">
                            <Phone className="size-3.5 text-muted-foreground" aria-hidden="true" />
                            {pick(lang, consultationCopy.labelPhone)}
                            {phoneDef?.required && <span className="text-destructive">*</span>}
                          </span>
                        </Label>
                        <Input id="consult_phone" type="tel" value={formData.phone} onChange={e => handleInputChange("phone", e.target.value)} placeholder={pick(lang, consultationCopy.placeholderPhone)} disabled={submitting} aria-invalid={Boolean(fieldErrors[phoneDef?.id || "phone"])} className="bg-card text-card-foreground" />
                        {fieldErrors[phoneDef?.id || "phone"] && <p className="mf-caption text-destructive mt-1 font-medium">
                            {fieldErrors[phoneDef?.id || "phone"].message}
                          </p>}
                      </div>
                    </div>

                    {/* Mesh Interest Dropdown */}
                    <div className="space-y-2">
                      <Label htmlFor="consult_mesh_interest" className="text-sm font-medium flex items-center gap-1.5 text-foreground">
                        <Layers className="size-3.5 text-muted-foreground" aria-hidden="true" />
                        {pick(lang, consultationCopy.labelMeshInterest)}
                        {meshInterestDef?.required && <span className="text-destructive">*</span>}
                      </Label>
                      <Select value={formData.mesh_interest} onValueChange={val => handleInputChange("mesh_interest", val)} disabled={submitting}>
                        <SelectTrigger id="consult_mesh_interest" className="w-full bg-card text-card-foreground" aria-invalid={Boolean(fieldErrors[meshInterestDef?.id || "mesh_interest"])}>
                          <SelectValue placeholder={pick(lang, consultationCopy.placeholderMeshInterest)} />
                        </SelectTrigger>
                        <SelectContent className="bg-popover text-popover-foreground">
                          {meshInterestDef?.options?.map((opt, i) => {
                        const optionLabel = categoryOptionLabels[opt.value] ? pick(lang, categoryOptionLabels[opt.value]) : opt.label;
                        return <SelectItem key={opt.value} value={opt.value} data-index={i}>
                                {optionLabel}
                              </SelectItem>;
                      })}
                        </SelectContent>
                      </Select>
                      {fieldErrors[meshInterestDef?.id || "mesh_interest"] && <p className="mf-caption text-destructive mt-1 font-medium">
                          {fieldErrors[meshInterestDef?.id || "mesh_interest"].message}
                        </p>}
                    </div>

                    {/* Project Specifications Textarea */}
                    <div className="space-y-2">
                      <Label htmlFor="consult_project_details" className="text-sm font-medium flex items-center justify-between text-foreground">
                        <span className="flex items-center gap-1.5">
                          <FileText className="size-3.5 text-muted-foreground" aria-hidden="true" />
                          {pick(lang, consultationCopy.labelProjectDetails)}
                          {projectDetailsDef?.required && <span className="text-destructive">*</span>}
                        </span>
                      </Label>
                      <Textarea id="consult_project_details" rows={4} value={formData.project_details} onChange={e => handleInputChange("project_details", e.target.value)} placeholder={pick(lang, consultationCopy.placeholderProjectDetails)} disabled={submitting} aria-invalid={Boolean(fieldErrors[projectDetailsDef?.id || "project_details"])} className="bg-card text-card-foreground min-h-28" />
                      {fieldErrors[projectDetailsDef?.id || "project_details"] && <p className="mf-caption text-destructive mt-1 font-medium">
                          {fieldErrors[projectDetailsDef?.id || "project_details"].message}
                        </p>}
                    </div>

                    {/* Action Row */}
                    <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                      <p className="mf-caption text-muted-foreground order-2 sm:order-1 text-center sm:text-left">
                        {pick(lang, consultationCopy.ndaNotice)}
                      </p>
                      <Button type="submit" disabled={submitting} size="lg" className="w-full sm:w-auto px-7 order-1 sm:order-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                        {submitting ? <>
                            <Loader2 className="size-4 animate-spin mr-2" aria-hidden="true" />
                            {pick(lang, consultationCopy.btnSubmitting)}
                          </> : <>
                            <Send className="size-4 mr-2" aria-hidden="true" />
                            {pick(lang, consultationCopy.btnSubmit)}
                          </>}
                      </Button>
                    </div>
                  </form>}
              </CardContent>
            </Card>
          </div>
        </Reveal>
      </div>
    </section>;
}